import { useCallback, useMemo, useState } from "react";

import { toFiniteNumber } from "components/ui/formatters";
import { ReportValueCardProps } from "helpers/types";

// What the row holds until somebody types a figure into it. One home, used
// both as the report's declared default and as what the row actually renders.
//
// It is `null` rather than a number BECAUSE the default is not a figure. The
// cards already render a nullish value as the muted "N/A" (MultiValuesCard
// says why) and toPdfValues already drops a nullish row from the export, so
// the two halves of the requirement -- N/A on screen, absent from the
// client's PDF -- both fall out of this one value rather than out of a flag
// beside it.
export const AVAILABLE_MARGIN_BALANCE_DEFAULT = null;

/**
 * The Protect card's Available Margin Balance row: N/A until an operator
 * types a figure, still editable, and the ONE field on the Strategy Report
 * that is not persisted.
 *
 * It lives in its own module for two reasons. Every other row on that report
 * is a `useStrategyReportValue` call and needs no explanation; this one is the
 * exception and carries the whole of why. And a row built inline inside a
 * two-thousand-line component cannot be pinned by a test — which matters
 * here more than usual, because the requirement is about what the field does
 * NOT do, and nothing observable is missing when it starts persisting again.
 *
 * WHY NOTHING IS STORED. Every other figure on this report is
 * localStorage-backed, so a number an operator types survives a reload. The
 * instruction for this one is that it reads N/A, which is a claim about what
 * the field shows on EVERY load — and a stored figure read back would break
 * that claim the first time anybody typed one. So the state is local and dies
 * with the page: no storage key, nothing written on edit, and the value left
 * under the retired `available_margin_balance_strategy` key in an existing
 * user's browser is never read again.
 *
 * WHY `null` RATHER THAN A NUMBER NOBODY CHOSE. This row used to default to a
 * hard-coded 0 wearing the muting that said so, which asked the muting to
 * carry the whole distinction: "$0.00" is a perfectly ordinary-looking figure
 * and the treatment was the only thing separating a placeholder zero from a
 * calculated one — on screen, and nothing at all separated them on paper,
 * where the line printed "$0.00" into a client's copy for a figure nobody had
 * worked out. Defaulting to no-figure-at-all removes the need for that
 * distinction instead of dressing it: N/A cannot be misread as an amount, and
 * the export drops the line rather than asserting a balance of zero.
 *
 * WHAT A TYPED FIGURE OUTLIVES, AND WHAT IT DOES NOT. It survives a report
 * RUN: "edits transient" was chosen over the option that cleared the field on
 * every run, so re-running the report for the same client and period leaves
 * the operator's figure alone. It does NOT survive a change of CLIENT or
 * PERIOD — `reset` is called from the report's own per-run reset effect, on
 * the rule that effect already states: the previous client's data must not
 * read as this client's. That rule bites hardest here, because this row is
 * the one that reaches a client-facing PDF as a hand-entered figure, so a
 * number typed for account A would print under account B's name with nothing
 * on the page to say it was not B's.
 *
 * AND THERE IS A WAY BACK. Clearing the box returns the row to N/A, through
 * the same `reset` the per-run effect calls — the editor reports an emptied
 * field through `onClear` rather than parsing "" to NaN and writing nothing,
 * which is what used to make a mistyped figure survive until a reload. A
 * default that is a STATE rather than a number needs that door: with no way
 * back, an operator who typed into this row could not get the client's PDF
 * to stop printing it.
 */
export const useAvailableMarginBalance = (): {
  row: Pick<ReportValueCardProps, "value">;
  onValueChange: (value: number | string) => void;
  reset: () => void;
} => {
  const [typed, setTyped] = useState<number | null>(null);

  // The card's setter contract is `number | string`, so the string half has
  // to be dealt with here. The editor only ever hands back a finite number
  // (EditableTextField calls this inside its own isNaN check, and routes an
  // emptied box to `reset` below instead), so the guard is UNREACHABLE today
  // and is here for the prop's union rather than for an observed case. It is
  // kept because what it prevents is the one combination that reads as a lie:
  // a non-figure recorded as the operator's would put an amount on the row --
  // and into the client's PDF -- that nobody typed.
  const onValueChange = useCallback((value: number | string) => {
    const figure = toFiniteNumber(value);
    if (figure === null) return;
    setTyped(figure);
  }, []);

  // Back to N/A. TWO callers, and they are the same operation for the same
  // reason: the report changing which client or period it is for (see the
  // block above for why this row in particular cannot be left standing across
  // that change), and the operator emptying the box.
  const reset = useCallback(() => setTyped(null), []);

  // Memoised so the row keeps its identity between renders, which is what
  // lets the Protect card's memoised rows hold theirs.
  const row = useMemo(() => availableMarginBalanceRow(typed), [typed]);

  return { row, onValueChange, reset };
};

/**
 * The state above as the prop that decides what the row shows: `null` is the
 * N/A the card mutes and the export omits, a number is the operator's own
 * figure in the figure treatment.
 *
 * Split out from the hook so the mapping can be read and pinned on its own,
 * and typed `number | null` rather than the report layer's usual
 * `number | string` — that is load-bearing: every persisted field hands back
 * `number | string` and can never be null, so re-wiring this row to
 * useStrategyReportValue does not typecheck.
 */
export const availableMarginBalanceRow = (
  typed: number | null,
): Pick<ReportValueCardProps, "value"> => ({
  value: typed ?? AVAILABLE_MARGIN_BALANCE_DEFAULT,
});
